import { useCallback, useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";
import { mvpApi } from "../../services/api";
import type { DocumentSectionAckKey } from "../utils/deriveDocumentChecklistHints";

interface DocumentFlowHelpSheetProps {
  open: boolean;
  onClose: () => void;
  /** When set (e.g. document checklist), advisors see section in case history; organizer leaves this unset. */
  sectionKey?: DocumentSectionAckKey | null;
  /** Localized page label shown to the applicant (and stored for advisor context via section when set). */
  pageTitle: string;
}

export default function DocumentFlowHelpSheet({
  open,
  onClose,
  sectionKey,
  pageTitle,
}: DocumentFlowHelpSheetProps) {
  const { t, isRTL } = useLang();
  const { caseSessionId, ensureCaseSession } = useForm();

  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setMessage("");
    setSent(false);
    setSending(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const sid = caseSessionId ?? (await ensureCaseSession());
      if (!sid) {
        setError(t("docFlow.helpSheetSessionError"));
        return;
      }
      await mvpApi.sendApplicantHelpMessage(
        sid,
        message.trim(),
        sectionKey != null ? sectionKey : undefined,
      );
      setSent(true);
      setMessage("");
    } catch (err) {
      console.error("Failed to send help message", err);
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("docFlow.helpSheetSendError"));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const sheetContent = (
    <div className={isRTL ? "text-right" : "text-left"}>
      <div className="flex items-start justify-between gap-3 mb-4" dir={isRTL ? "rtl" : "ltr"}>
        <div className="flex-1 min-w-0">
          <h2 id="doc-flow-help-title" className="text-[17px] font-semibold text-slate-900 leading-snug">
            {t("docFlow.helpSheetTitle")}
          </h2>
          <p className="text-[13px] text-slate-500 mt-2 leading-snug">{t("docFlow.helpSheetSubtitle")}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
          aria-label={t("documentScanner.close")}
        >
          <i className="ri-close-line text-xl" aria-hidden />
        </button>
      </div>

      <p className="text-[12px] text-slate-500 italic mb-4 leading-snug" dir={isRTL ? "rtl" : "ltr"}>
        {t("docFlow.helpSheetContextPrefix")}
        {!isRTL ? " " : "\u00A0"}
        <span className="font-medium text-slate-700 not-italic">{pageTitle}</span>
      </p>

      {sent ? (
        <div
          className={`flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/90 px-4 py-3 mb-4 ${
            isRTL ? "flex-row-reverse" : ""
          }`}
          role="status"
        >
          <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center shrink-0 shadow-sm">
            <i className="ri-check-line text-white text-xl leading-none" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-emerald-900 leading-snug">{t("docFlow.helpSheetSentTitle")}</p>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-3">{error}</p>
      ) : null}
      <textarea
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
          if (sent) setSent(false);
        }}
        placeholder={t("docFlow.helpSheetPlaceholder")}
        maxLength={500}
        rows={4}
        className={`w-full px-3 py-2.5 border border-slate-200 rounded-xl text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 transition-all resize-y min-h-[110px] bg-white mb-3 ${
          isRTL ? "text-right" : ""
        }`}
        dir={isRTL ? "rtl" : "ltr"}
      />
      <button
        type="button"
        onClick={() => void handleSend()}
        disabled={!message.trim() || sending}
        className="w-full min-h-[48px] rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-[15px] font-semibold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {sending ? t("docFlow.helpSheetSending") : t("docFlow.helpSheetSend")}
      </button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col justify-end"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
      role="presentation"
    >
      <style>{`@keyframes docHelpSheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      <div
        className="w-full max-w-[480px] mx-auto bg-white rounded-t-3xl px-5 pt-3 pb-8 shadow-2xl"
        style={{ animation: "docHelpSheetUp 0.28s cubic-bezier(0.32,0.72,0,1)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-flow-help-title"
      >
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" aria-hidden />
        {sheetContent}
      </div>
    </div>
  );
}
